
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __appWithI18n from 'next-translate/appWithI18n'
// @ts-ignore
    
import { Header } from '@lushdigital/header/components/header';
import LushLoader from '@lushdigital/ui/lushLoader';
import DashboardTiles from '@tills/components/dashboard-tiles';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { SessionProvider } from 'next-auth/react';
import useTranslation from 'next-translate/useTranslation';
import React, { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { IntlProvider } from 'react-intl';
import { QueryCache, QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import CookieBanner from '@/components/CookieBanner';

import '../styles/global.css';
import '../scss/app.scss';
import '@lushdigital/ui-latest/component/molecules/fancyTable/style.scss';
import '@lushdigital/ui-latest/component/molecules/inlineMenu/style.scss';
// eslint-disable-next-line import/no-absolute-path
import '/node_modules/@salocreative/react-pagination/src/scss/styles.scss';

import dynamic from 'next/dynamic';
import { ApiError, UserAuthError } from '@/manager-next/customErrors';
import { clarity } from '@/manager-next/helpers/clarity';
import { logSentryApiException } from '@/manager-next/helpers/sentry';
import { FeatureProvider, useFeatures } from '@/tills/context/features';

const StoreTimezone = dynamic(() => import('@/components/StoreTimezone'), {
  ssr: false
});

const { NEXT_PUBLIC_SENTRY_RELEASE } = process.env;
const ENV = process.env.NEXT_PUBLIC_BUILD_ENV || process.env.NODE_ENV;

/**
 * Not using feature flags for this because it's instantiated outside of a component,
 * and because it should NEVER be used outside of development. Tests have their own
 * version (in /mocks).
 */
if (process.env.NODE_ENV === 'development') {
  // eslint-disable-next-line global-require, @typescript-eslint/no-var-requires
  require('../mocks/index');
}

const Loading = (): JSX.Element | null => {
  const router = useRouter();

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleStart = () => {
      setLoading(true);
      return true;
    };
    const handleComplete = () => {
      setLoading(false);
      return true;
    };

    router.events.on('routeChangeStart', handleStart);
    router.events.on('routeChangeComplete', handleComplete);
    router.events.on('routeChangeError', handleComplete);

    return () => {
      router.events.off('routeChangeStart', handleStart);
      router.events.off('routeChangeComplete', handleComplete);
      router.events.off('routeChangeError', handleComplete);
    };
  }, [router.events]);

  useEffect(() => {
    if (loading) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [loading]);

  if (loading) {
    return (
      <div
        style={{
          position: 'absolute',
          zIndex: 999,
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'white',
          display: 'grid',
          placeItems: 'center'
        }}
      >
        <LushLoader width={300} />
      </div>
    );
  }

  return null;
};

const LayoutWrapper = ({ children, hideNav }): JSX.Element => {
  const { t } = useTranslation('tills');
  const features = useFeatures();

  return (
    <>
      <Head>
        <link rel="shortcut icon" type="image/png" href="/assets/img/favicon.png" />
      </Head>
      {!hideNav && <Header dashboard={<DashboardTiles />} title={t('SHOP_MANAGER')} />}
      {features.featureFlags?.includes('displayStoreTime') && <StoreTimezone />}

      <Loading />

      {children}

      <CookieBanner />

      {process.env.NEXT_PUBLIC_BUILD_ENV === 'production' ? (
        <>
          <Script
            src={`https://www.googletagmanager.com/gtag/js?id=${process.env.NEXT_PUBLIC_GA_ID}`}
            strategy="afterInteractive"
          />
          <Script id="google-analytics" strategy="afterInteractive">
            {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){window.dataLayer.push(arguments);}
            gtag('js', new Date());

            gtag('config', '${process.env.NEXT_PUBLIC_GA_ID}');
          `}
          </Script>
        </>
      ) : null}
    </>
  );
};

const MyApp = ({ Component, pageProps: { session, ...pageProps } }): JSX.Element => {
  const router = useRouter();
  const { locale } = router;

  const [queryClient] = useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: (error) => {
            logSentryApiException({
              exception: error
            });

            if (error instanceof UserAuthError) {
              console.warn('Redirect in _app - user auth error');
              if (clarity.hasStarted()) {
                clarity.setTag('warning', 'UserAuthError');
              }
              router.push('/login', undefined, { locale });
            } else if (error instanceof ApiError) {
              console.error('API error', error);
              if (clarity.hasStarted()) {
                clarity.setTag('error', 'APIError');
              }
            } else {
              console.error('Unknown error', error);
              if (clarity.hasStarted()) {
                clarity.setTag('error', 'UnknownError');
              }
            }
          }
        })
      })
  );

  useEffect(() => {
    const isLoginPage = router.pathname === '/login';
    const isAccessDeniedPage = router.pathname === '/access-denied';

    if (!session && !isLoginPage && !isAccessDeniedPage) {
      console.warn('Redirect in _app - no session');
      if (clarity.hasStarted()) {
        clarity.setTag('warning', 'RedirectToLoginNoSession');
      }
      router.push('/login', undefined, { locale });
    }
  }, []);

  const errorComp = () => (
    <>
      <h2>An error occured. Please clear your cookies and login again.</h2>
      <p>If the error persists please contact an administrator</p>
    </>
  );

  console.warn('Manager', ENV, NEXT_PUBLIC_SENTRY_RELEASE);

  const localeMap = {
    ja: 'ja',
    de: 'de',
    en: 'en',
    us: 'en-US',
    lb: 'lb',
    be: 'nl-BE',
    fr: 'fr',
    nl: 'nl-NL'
  };

  return (
    <QueryClientProvider client={queryClient}>
      <SessionProvider session={session}>
        <IntlProvider locale={locale ? localeMap[locale] : 'EN'}>
          <FeatureProvider>
            <LayoutWrapper hideNav={Component.hideNav}>
              <ErrorBoundary
                FallbackComponent={errorComp}
                onError={(error) => {
                  logSentryApiException({
                    exception: error,
                    config: {
                      url: window.location.href,
                      data: {
                        locale,
                        expires: session.expires,
                        shop: session.shop,
                        session_user: session.user
                      }
                    }
                  });
                }}
              >
                <Component {...pageProps} />
              </ErrorBoundary>
            </LayoutWrapper>
          </FeatureProvider>
        </IntlProvider>
      </SessionProvider>
      {process.env.NEXT_PUBLIC_BUILD_ENV === 'development' ? <ReactQueryDevtools initialIsOpen={false} /> : null}
    </QueryClientProvider>
  );
};

// Only uncomment this method if you have blocking data requirements for
// every single page in your application. This disables the ability to
// perform automatic static optimization, causing every page in your app to
// be server-side rendered.
//
// MyApp.getInitialProps = async (appContext) => {
//   // calls page's `getInitialProps` and fills `appProps.pageProps`
//   const appProps = await App.getInitialProps(appContext);
//
//   return { ...appProps }
// }

const __Page_Next_Translate__ = MyApp;


// @ts-ignore
    export default __appWithI18n(__Page_Next_Translate__, {
// @ts-ignore
      ...__i18nConfig,
// @ts-ignore
      isLoader: true,
// @ts-ignore
      skipInitialProps: false,
// @ts-ignore
      loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
// @ts-ignore
    });
// @ts-ignore
  